import {Markers} from '@/services/MapService';
import {Graphics} from '@/services/GraphicService';
import {getStore} from '@/services/store';
import {MapService, gmAPI} from '@/services/MapService';
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {geohashForLocation} from 'geofire-common';
import {TokenService} from '@/services/TokenService';
import {MVTLayers} from '@/services/MapService/MVTLayers';
import {calculateSF, calculateTotalAcreage} from '../../PolyGraphic/PolygonGraphic';
import {convertEsriRingsToLatLng} from './esriUtils';

import type {Overlay} from '@/services/OverlaysService';
import type {MapLayer} from '@/services/GraphicService/MapLayer';
import {buildParcelDisplayFields, fetchZoningComps} from './lightbox';

/** The Firestore ID for the Lightbox Parcels layer in beyondMaps-layers */
const LIGHTBOX_PARCELS_LAYER_ID = 'Lightbox Parcels';

/** Lightweight selection state — no FeatureServer data until "Add to Overlay" is confirmed */
interface SelectedParcel {
  id: string;
  polygon: google.maps.Polygon;
}

const _selectedParcels = new Map<string, SelectedParcel>();
let _layerWasVisible: boolean;
let _overlay: Overlay | null;
let _finishedCallback: () => void;
let _lightboxLayer: MapLayer | null;

const _findLightboxLayer = (): MapLayer | null => {
  const {userLayers, layers} = getStore().state.layers;
  // First look for the layer in userLayers, since this is where the MapLayer instance will be if the user has
  // enabled it.
  return userLayers.find((l: MapLayer) => l.id === LIGHTBOX_PARCELS_LAYER_ID)
    ?? layers.find((l: MapLayer) => l.id === LIGHTBOX_PARCELS_LAYER_ID)
    ?? null;
};

const _userHasLayerEnabled = (): boolean => {
  return getStore().state.layers.userLayers.find((l: MapLayer) => l.id === LIGHTBOX_PARCELS_LAYER_ID)?.checked
    ?? false;
};

const handleAddMultipleParcels = async (overlay: Overlay, finishedCallback: () => void) => {
  _overlay = overlay;
  _finishedCallback = finishedCallback;

  Markers.setAllClickable(false);
  Graphics.setAllClickable(false);

  getStore().commit('setSnack', {
    duration: -1,
    message: 'Loading parcels...',
  });

  _lightboxLayer = _findLightboxLayer();
  if (!_lightboxLayer) {
    console.warn('Lightbox Parcels layer not found in layers config.');
    getStore().commit('setSnack', {
      message: 'Parcels layer is not available.',
      color: 'error',
    });
    _exitAddMultipleParcels();
    return _exitAddMultipleParcels;
  }

  const layerEnabled = _userHasLayerEnabled();
  _layerWasVisible = layerEnabled;

  await _lightboxLayer.initialize();
  if (!_layerWasVisible) {
    _lightboxLayer.setMap(MapService.getMap());
  }

  // Re-apply non-clickable state so the newly initialized layer graphic
  // does not open an info window when clicked during multi-select mode.
  Graphics.setAllClickable(false);

  MapService.addMapListener('addMultipleParcelsClick', 'click', _handleMapClick);

  // After the layer is loaded, add a listener to check if the user zooms out too far
  MapService.addMapListener('addMultipleParcelsMapIdle', 'idle', () => {
    if ((MapService.getMap().getZoom() ?? 0) < 14) {
      _exitAddMultipleParcels();
    }
  });

  _updateParcelSnack();

  return _exitAddMultipleParcels;
};

/**
 * Handle map clicks during multi-select mode.
 * Picks PARCEL_LID from the MVT layer, then fetches full geometry from the FeatureServer
 * so the highlight polygon covers the entire parcel (not clipped to tile boundaries).
 */
const _handleMapClick = async (event: google.maps.MapMouseEvent) => {
  if (!_lightboxLayer) return;
  const layerName = _lightboxLayer.name;
  if (!layerName) return;

  const features = MVTLayers.getLayerFeaturesAtPosition(layerName, {
    x: (event.domEvent as MouseEvent).pageX,
    y: (event.domEvent as MouseEvent).pageY,
  });
  if (!features?.length) return;

  const parcelLids = [...new Set(
    features
      .map((f) => (f?.object?.properties as Record<string, string>)?.PARCEL_LID)
      .filter(Boolean),
  )];
  if (!parcelLids.length) return;

  const toDeselect = parcelLids.filter((lid) => _selectedParcels.has(lid));
  const toSelect = parcelLids.filter((lid) => !_selectedParcels.has(lid));

  toDeselect.forEach((lid) => _deselectParcel(lid));

  if (!toSelect.length) return;

  // Fetch full geometry from the FeatureServer for accurate highlight polygons
  MapService.setCursor('wait');
  try {
    const token = await TokenService.getToken(_lightboxLayer.tokenName);
    const headers = {'Authorization': `Bearer ${token}`};
    const featureServerUrl = _lightboxLayer.urlMap['featureServer'];

    for (const parcelLid of toSelect) {
      const q = new URLSearchParams({
        f: 'json',
        where: `PARCEL_LID='${parcelLid}'`,
        outFields: 'PARCEL_LID',
        returnGeometry: 'true',
        token,
      });
      const response = await fetch(`${featureServerUrl}/3/query?${q.toString()}`, {method: 'GET', headers});
      if (!response.ok) continue;
      const json = await response.json();
      const feature = json.features?.[0];
      if (!feature?.geometry?.rings) continue;

      const paths = convertEsriRingsToLatLng(feature.geometry.rings, json.spatialReference);
      const poly = new gmAPI.Polygon({
        paths,
        fillColor: 'rgb(144,238,144)',
        strokeColor: 'rgb(200,0,0)',
      });

      _selectParcel({id: parcelLid, polygon: poly});
    }
  } finally {
    MapService.setCursor('default');
  }
};

/** Add a parcel to the selection and highlight it on the map */
const _selectParcel = (parcel: SelectedParcel) => {
  _selectedParcels.set(parcel.id, parcel);
  parcel.polygon.setMap(MapService.getMap());
  parcel.polygon.addListener('click', () => _deselectParcel(parcel.id));
  parcel.polygon.setOptions({clickable: true, zIndex: 1000});
  _updateParcelSnack();
};

/** Remove a parcel from the selection and remove its highlight */
const _deselectParcel = (parcelId: string) => {
  const parcel = _selectedParcels.get(parcelId);
  if (parcel) {
    parcel.polygon.setMap(null);
    _selectedParcels.delete(parcelId);
  }
  _updateParcelSnack();
};

/** Update the snackbar whenever the selected parcels change */
const _updateParcelSnack = () => {
  const selectedParcelCount = _selectedParcels.size;
  if (selectedParcelCount === 0) {
    getStore().commit('setSnack', {
      duration: -1,
      message: 'Click to select multiple parcels',
      closeCallback: _exitAddMultipleParcels,
    });
    return;
  }
  const snackMessage = `${selectedParcelCount} parcel${selectedParcelCount > 1 ? 's are' : ' is'} selected`;
  getStore().commit('setSnack', {
    duration: -1,
    message: snackMessage,
    action: {text: 'Add to Overlay', callback: _addParcelsToOverlay},
    closeCallback: _exitAddMultipleParcels,
  });
};

/** Exit the mode where you can select multiple parcels */
const _exitAddMultipleParcels = () => {
  Markers.setAllClickable(true);
  Graphics.setAllClickable(true);

  _selectedParcels.forEach((parcel) => {
    parcel.polygon.setMap(null);
  });
  _selectedParcels.clear();

  if (!_layerWasVisible && _lightboxLayer) {
    _lightboxLayer.setMap(null);
  }

  MapService.removeMapListener('addMultipleParcelsClick');
  MapService.removeMapListener('addMultipleParcelsMapIdle');

  getStore().commit('setSnack', {duration: 0});
};

/**
 * Add all selected parcels to the overlay.
 * Fetches full parcel data from the FeatureServer at this point.
 */
const _addParcelsToOverlay = async () => {
  if (!_overlay || !_lightboxLayer) {
    console.warn('addMultipleParcels failed because there is no overlay or layer.');
    return;
  }

  const parcelIds = Array.from(_selectedParcels.keys());
  if (!parcelIds.length) return;

  getStore().commit('setSnack', {
    duration: -1,
    message: `Adding ${parcelIds.length} parcel${parcelIds.length > 1 ? 's' : ''} to overlay...`,
  });

  MapService.setCursor('wait');
  try {
    const token = await TokenService.getToken(_lightboxLayer.tokenName);
    const headers = {'Authorization': `Bearer ${token}`};
    const featureServerUrl = _lightboxLayer.urlMap['featureServer'];

    for (const parcelLid of parcelIds) {
      await _fetchAndAddParcelToOverlay(parcelLid, featureServerUrl, token, headers);
    }

    _finishedCallback();
  } catch (error) {
    console.error('Error adding parcels to overlay:', error);
    getStore().commit('setSnack', {
      message: 'Error adding parcels to overlay.',
      color: 'error',
    });
  } finally {
    MapService.setCursor('default');
    _exitAddMultipleParcels();
  }
};

/**
 * Fetches a single parcel's full attributes and geometry from the FeatureServer,
 * then creates a new feature in the overlay.
 */
const _fetchAndAddParcelToOverlay = async (
  parcelLid: string,
  featureServerUrl: string,
  token: string,
  headers: Record<string, string>,
) => {
  if (!_overlay) return;

  const q = new URLSearchParams({
    f: 'json',
    where: `PARCEL_LID='${parcelLid}'`,
    outFields: '*',
    returnGeometry: 'true',
    token,
  });

  const paResponse = await fetch(`${featureServerUrl}/3/query?${q.toString()}`, {method: 'GET', headers});
  if (!paResponse.ok) return;
  const paJson = await paResponse.json();
  const feature = paJson.features?.[0];
  if (!feature) return;

  const parcelDisplayFields = buildParcelDisplayFields(feature.attributes);

  const comps = await fetchZoningComps(featureServerUrl, q.toString(), headers);
  const helper = (field: DisplayField) => field.value !== null && field.value !== undefined;
  comps.forEach((comp) => comp.displayFields = comp.displayFields.filter(helper));

  const parcelGeometry = convertEsriRingsToLatLng(feature.geometry?.rings, paJson.spatialReference);

  // Serialize polygon paths for Firestore
  const bounds = new gmAPI.LatLngBounds();
  const polys: {[key: number]: {lat: number; lng: number}[]} = {};
  parcelGeometry.forEach((ring, i) => {
    const path: {lat: number; lng: number}[] = [];
    ring.forEach((point) => {
      path.push({lat: point.lat(), lng: point.lng()});
      bounds.extend(point);
    });
    polys[i] = path;
  });

  // Calculate geohash and generated fields
  const center = bounds.getCenter();
  const geohash = geohashForLocation([center.lat(), center.lng()]);
  const paths = Object.values(polys);
  let displayFields = [
    ...parcelDisplayFields,
    new DisplayField({
      unit: 'acreage',
      allowEdit: false,
      allowLabelEdit: false,
      label: 'Acreage Calculated',
      value: calculateTotalAcreage(paths),
    }),
    new DisplayField({
      unit: 'squareFeet',
      allowEdit: false,
      allowLabelEdit: false,
      label: 'Calculated SF',
      value: calculateSF(paths),
    }),
  ];
  displayFields = displayFields.filter((df) => df.value !== null && df.value !== undefined);

  await _overlay.newFeature({
    name: feature.attributes['OWNER_NAME'] ?? 'Parcel',
    displayFields,
    comps,
    bounds: bounds.toJSON(),
    geohash,
    graphicData: {
      featureType: 'polygon',
      clickable: true,
      bounds: bounds.toJSON(),
      polys,
    },
  });
};

export {
  handleAddMultipleParcels,
  // exported for testing purposes:
  _selectedParcels,
  _selectParcel,
  _deselectParcel,
};
